import * as yup from 'yup';

export const addressSchema = yup.object({
  name: yup.string().optional(), // Company name or premises name
  line1: yup.string().required('Address line 1 is required'),
  line2: yup.string().optional(),
  city: yup.string().required('City is required'),
  postcode: yup.string().required('Postcode is required'),
});

export const personSchema = yup.object({
  fullName: yup.string().required('').test(
    'is-full-name',
    'Please enter at least a first and last name (e.g. "John Smith")',
    (value) => {
      if (!value) return false;
      return value.trim().indexOf(' ') !== -1;
    }
  ),
  address: addressSchema,
  relationship: yup.string().required('Relationship is required'),
});

export const guardianSchema = yup.object({
  fullName: yup.string().required('').test(
    'is-full-name',
    'Please enter at least a first and last name (e.g. "John Smith")',
    (value) => {
      if (!value) return false;
      return value.trim().indexOf(' ') !== -1;
    }
  ),
  address: addressSchema,
  relationship: yup.string().required('Relationship is required'),
  forChild: yup.string().optional(), // Which child this guardian is for
});

export const personalDetailsNameSchema = yup.object({
  fullName: yup.string().required('').test(
    'is-full-name',
    'Please enter at least a first and last name (e.g. "John Smith")',
    (value) => {
      if (!value || value.trim() === '') return true; // Don't show error if empty
      return value.trim().indexOf(' ') !== -1; // Only show error if has text but no space
    }
  ),
  email: yup.string().email('Please enter a valid email').optional(),
  otherNames: yup.string().optional(),
  address: addressSchema,
  dateOfBirth: yup.string().required('Date of birth is required')
    .test('format', 'Enter a valid date in DD/MM/YYYY', (value) => {
      if (!value) return false;
      // Enforce 2 digits / 2 digits / 4 digits
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false;

      const [dayStr, monthStr, yearStr] = value.split('/');
      const day = parseInt(dayStr, 10);
      const month = parseInt(monthStr, 10) - 1;
      const year = parseInt(yearStr, 10);

      // Year bounds: realistic and not in the future
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) return false;

      const dob = new Date(year, month, day);
      // Check that JS date matches input (catches invalid dates like 32/13/2020)
      return dob.getFullYear() === year && dob.getMonth() === month && dob.getDate() === day;
    })
    .test('is-18', 'You must be 18 or older to make a Will', (value) => {
      if (!value) return false;
      // Parse DD/MM/YYYY
      const parts = value.split('/');
      if (parts.length !== 3) return false;

      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
      const year = parseInt(parts[2], 10);

      const dob = new Date(year, month, day);
      if (isNaN(dob.getTime())) return false;

      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      return age >= 18;
    }),
});

export const personalDetailsStatusSchema = yup.object({
  maritalStatus: yup.string().oneOf([
    'single',
    'married',
    'civil_partnership',
    'cohabiting',
    'engaged',
    'widowed',
    'remarried',
    'separated',
    'divorced'
  ], 'Please select a valid option').required('Marital status is required'),
  spouseFullName: yup.string().when('maritalStatus', {
    is: (val: string) => ['married', 'civil_partnership', 'remarried', 'separated', 'cohabiting'].includes(val),
    then: (schema) => schema.required('').test(
      'is-full-name',
      'Please enter at least a first and last name (e.g. "John Smith")',
      (value) => {
        if (!value || value.trim() === '') return true;
        return value.trim().indexOf(' ') !== -1;
      }
    ),
    otherwise: (schema) => schema.notRequired(),
  }),
  spouseLivesWithYou: yup.boolean().when('maritalStatus', {
    is: (val: string) => ['married', 'civil_partnership', 'remarried', 'separated', 'cohabiting'].includes(val),
    then: (schema) => schema.required('Please specify if partner lives with you'),
    otherwise: (schema) => schema.notRequired(),
  }),
  spouseAddress: addressSchema.when(['maritalStatus', 'spouseLivesWithYou'], {
    is: (maritalStatus: string, spouseLivesWithYou: boolean) => ['married', 'civil_partnership', 'remarried', 'separated', 'cohabiting'].includes(maritalStatus) && spouseLivesWithYou === false,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired().default(undefined),
  }),
  planningToMarry: yup.boolean().optional(),
  intendedPartnerFullName: yup.string().when(['maritalStatus', 'planningToMarry'], {
    is: (status: string, planning: boolean) => status === 'engaged' || planning === true,
    then: (schema) => schema.required("Intended Partner's Full Name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  intendedPartnerLivesWithYou: yup.boolean().when(['maritalStatus', 'planningToMarry'], {
    is: (status: string, planning: boolean) => status === 'engaged' || planning === true,
    then: (schema) => schema.required('Please specify if partner lives with you'),
    otherwise: (schema) => schema.notRequired(),
  }),
  intendedPartnerAddress: addressSchema.when(['maritalStatus', 'planningToMarry', 'intendedPartnerLivesWithYou'], {
    is: (status: string, planning: boolean, livesWithYou: boolean) => (status === 'engaged' || planning === true) && livesWithYou === false,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired().default(undefined),
  }),
});

export const personalDetailsStep1Schema = personalDetailsNameSchema.concat(personalDetailsStatusSchema);

export const personalDetailsStep2Schema = yup.object({
  understandsSurvivorship: yup.boolean().optional(),
});

export const personalDetailsSchema = personalDetailsStep1Schema.concat(personalDetailsStep2Schema);

export const executorSchema = yup.object({
  fullName: yup.string().optional(), // Optional for professional executors
  companyName: yup.string().optional(),
  email: yup.string().email('Please enter a valid email').optional(),
  relationship: yup.string().optional(),
  address: addressSchema,
});

export const executorFormSchema = executorSchema.shape({
  isOver18: yup.boolean().oneOf([true], 'You must confirm the executor is over 18'),
});

export const childSchema = yup.object({
  fullName: yup.string().required('').test(
    'is-full-name',
    'Please enter at least a first and last name (e.g. "John Smith")',
    (value) => {
      if (!value) return false;
      return value.trim().indexOf(' ') !== -1;
    }
  ),
  dateOfBirth: yup.string().required('Date of birth is required').test(
    'format',
    'Enter a valid date in DD/MM/YYYY',
    (value) => {
      if (!value) return false;
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false;
      const [dayStr, monthStr, yearStr] = value.split('/');
      const day = parseInt(dayStr, 10);
      const month = parseInt(monthStr, 10);
      const year = parseInt(yearStr, 10);
      if (year < 1900 || year > 3000) return false;
      if (month < 1 || month > 12) return false;
      if (day < 1 || day > 31) return false;
      const date = new Date(year, month - 1, day);
      // Ensure the date matches (handles 31 Feb, etc.)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return false;
      }
      // Must not be in the future
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date <= today;
    }
  ),
});

export const beneficiarySchema = yup.object({
  fullName: yup.string().when('relationship', {
    is: 'Charity',
    then: (schema) => schema.required('Charity name is required'),
    otherwise: (schema) => schema.required('Full legal name is required').test(
      'is-full-name',
      'Please enter at least a first and last name (e.g. "John Smith")',
      (value) => {
        if (!value || value.trim() === '') return true;
        return value.trim().indexOf(' ') !== -1;
      }
    ),
  }),
  relationship: yup.string().optional(),
  percentage: yup.number()
    .typeError('Percentage must be a number')
    .min(1, 'Percentage must be at least 1%')
    .max(100, 'Percentage cannot exceed 100%')
    .required('Percentage is required')
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) return 1;
      return isNaN(value) ? 1 : value;
    }),
  isOver18: yup.boolean().nullable().optional().transform((value, originalValue) => {
    if (originalValue === '') return null;
    return value;
  }),
  predeceasePolicy: yup.string().nullable().optional().transform((value, originalValue) => {
    if (originalValue === '') return undefined;
    return value;
  }).oneOf(['redistribute', 'children', 'substitute', 'similar_goals', null, undefined]),
  charityNumber: yup.string().optional(),
  address: yup.object().when('relationship', {
    is: 'Charity',
    then: (schema) => schema.optional().nullable(),
    otherwise: (schema) => addressSchema
  }),
  substitutes: yup.array().optional(),
});

const optionalAddressSchema = yup.object({
  name: yup.string().optional(),
  line1: yup.string().optional(),
  line2: yup.string().optional(),
  city: yup.string().optional(),
  postcode: yup.string().optional(),
});

const charityRecipientSchema = yup.object({
  fullName: yup.string().required('Charity name is required'),
  address: optionalAddressSchema,
  relationship: yup.string().optional(),
});

// Simple recipient schema for gifts - only requires name
const giftRecipientSchema = yup.object({
  fullName: yup.string().required('Recipient name is required'),
  address: addressSchema,
  relationship: yup.string().optional(),
});

export const substituteRecipientSchema = yup.object({
  fullName: yup.string().required('').test(
    'is-full-name',
    'Please enter at least a first and last name (e.g. "John Smith")',
    (value) => {
      if (!value) return false;
      return value.trim().indexOf(' ') !== -1;
    }
  ),
  relationship: yup.string().required('Relationship is required'),
  address: addressSchema,
});

export const giftSchema = yup.object({
  type: yup.string().oneOf(['individual', 'charity']).default('individual'),

  // Individual Gift Specifics
  giftType: yup.string().oneOf(['item', 'cash']).when('type', {
    is: 'individual',
    then: (schema) => schema.required('Please select gift type'),
    otherwise: (schema) => schema.optional()
  }),

  description: yup.string().when(['type', 'giftType'], {
    is: (type: string, giftType: string) => type === 'individual' && giftType === 'item',
    then: (schema) => schema.required('Description is required'),
    otherwise: (schema) => schema.optional()
  }),

  value: yup.string().optional(), // Estimated value for item

  cashAmount: yup.number().when(['type', 'giftType'], {
    is: (type: string, giftType: string) => (type === 'individual' && giftType === 'cash') || type === 'charity',
    then: (schema) => schema.typeError('Amount must be a number').required('Amount is required'),
    otherwise: (schema) => schema.optional()
  }),

  charityNumber: yup.string().optional(),

  recipient: yup.object().when('type', {
    is: 'charity',
    then: () => charityRecipientSchema,
    otherwise: () => giftRecipientSchema,
  }),

  // What If - Recipient death (Individual)
  recipientFailPolicy: yup.string().oneOf(['fail', 'substitute', 'children']).default('fail'),
  substituteRecipient: yup.mixed().when('recipientFailPolicy', {
    is: 'substitute',
    then: () => substituteRecipientSchema,
    otherwise: () => yup.mixed().notRequired()
  }),

  // What If - Charity exists (Charity)
  charityFailPolicy: yup.string().oneOf(['fail', 'similar_aims']).default('fail'),
});

export const fallbackBeneficiarySchema = yup.object({
  type: yup.string().oneOf(['individual', 'charity', 'children', 'redistribute']).default('individual'),
  fullName: yup.string().when('type', {
    is: (type: string) => type === 'children' || type === 'redistribute',
    then: (schema) => schema.optional(),
    otherwise: (schema) => schema.when('type', {
      is: 'individual',
      then: (s) => s.required('Full legal name is required'),
      otherwise: (s) => s.required('Charity name is required'),
    }),
  }),
  relationship: yup.string().optional(),
  address: yup.object().when('type', {
    is: (type: string) => type === 'charity' || type === 'children' || type === 'redistribute',
    then: () => optionalAddressSchema,
    otherwise: () => addressSchema
  }),
  percentage: yup.number()
    .typeError('')
    .min(1)
    .max(100)
    .required(),
  charityNumber: yup.string().optional(),
});

export const petSchema = yup.object({
  name: yup.string().required(),
});

export const exclusionSchema = yup.object({
  fullName: yup.string().required('Full name is required'),
});

export const disasterFallbackSchema = yup.object({
  option: yup.string().oneOf(['descendants', 'parents_or_siblings', 'charity', 'intestacy']).optional(),
  charityName: yup.string().optional(),
  charityNumber: yup.string().optional(),
});

export const willSchema = yup.object({
  personalDetails: personalDetailsSchema,
  executors: yup.array().of(executorSchema).min(1, 'At least one executor is required').required(),
  substituteExecutors: yup.array().of(executorSchema).optional().default([]),
  hasChildren: yup.boolean().optional(),
  children: yup.array().of(childSchema).when('hasChildren', {
    is: true,
    then: (schema) => schema.min(1, 'Please add your children').required(),
    otherwise: (schema) => schema.notRequired().default([]),
  }),
  hasPets: yup.boolean().optional(),
  pets: yup.array().of(petSchema).when('hasPets', {
    is: true,
    then: (schema) => schema.min(1, 'Please add at least one pet').required(),
    otherwise: (schema) => schema.notRequired().default([]),
  }),
  appointGuardians: yup.boolean().optional(),
  guardians: yup.array().of(guardianSchema).when(['hasChildren', 'appointGuardians'], {
    is: (hasChildren: boolean, appointGuardians: boolean) => hasChildren === true && appointGuardians === true,
    then: (schema) => schema.min(1, 'Please appoint at least one guardian for your children').required(),
    otherwise: (schema) => schema.notRequired().default([]),
  }),
  appointPetGuardians: yup.boolean().optional(),
  petGuardians: yup.array().of(personSchema).when(['hasPets', 'appointPetGuardians'], {
    is: (hasPets: boolean, appointPetGuardians: boolean) => hasPets === true && appointPetGuardians === true,
    then: (schema) => schema.min(1, 'Please appoint at least one guardian for your pets').required(),
    otherwise: (schema) => schema.notRequired().default([]),
  }),
  gifts: yup.array().of(giftSchema).optional().default([]),
  beneficiaries: yup.array().of(beneficiarySchema).min(1, 'At least one beneficiary is required').required()
    .test('sum-100', 'Beneficiary shares must total 100%', (value) => {
      if (!value) return true;
      const total = value.reduce((sum, b) => sum + (b.percentage || 0), 0);
      return Math.abs(total - 100) < 0.1;
    }),
  exclusions: yup.array().of(exclusionSchema).optional().default([]),
  excludeChoice: yup.string().oneOf(['yes', 'no']).optional().nullable(),
  exclusionMessage: yup.string().optional(),
  guardianConfirmations: yup.object().optional(),
  funeralWishes: yup.string().optional(),
  additionalInstructions: yup.string().optional(),
  disasterFallback: disasterFallbackSchema.optional(),
  estateCompleted: yup.boolean().optional(),
  manuallyAddedPeople: yup.array().of(personSchema).optional().default([]),
  status: yup.string().optional(),
});

export const callbackRequestSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string()
    .required('Phone number is required')
    .matches(
      /^(?:(?:\(?(?:0(?:0|11)\)?[\s-]?\(?|\+)44\)?[\s-]?(?:\(?0\)?[\s-]?)?)|(?:\(?0))(?:(?:\d{5}\)?[\s-]?\d{4,5})|(?:\d{4}\)?[\s-]?(?:\d{5}|\d{3}[\s-]?\d{3}))|(?:\d{3}\)?[\s-]?\d{3}[\s-]?\d{3,4})|(?:\d{2}\)?[\s-]?\d{4}[\s-]?\d{4}))(?:[\s-]?(?:x|ext\.?|\#)\d{3,4})?$/,
      'Please enter a valid UK phone number'
    ),
  reason: yup.string().required('Reason is required'),
  source: yup.string().required('Source is required'),
  notes: yup.string().notRequired(),
});

export const willCallbackRequestSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string()
    .required('Phone number is required')
    .matches(
      /^(?:(?:\(?(?:0(?:0|11)\)?[\s-]?\(?|\+)44\)?[\s-]?(?:\(?0\)?[\s-]?)?)|(?:\(?0))(?:(?:\d{5}\)?[\s-]?\d{4,5})|(?:\d{4}\)?[\s-]?(?:\d{5}|\d{3}[\s-]?\d{3}))|(?:\d{3}\)?[\s-]?\d{3}[\s-]?\d{3,4})|(?:\d{2}\)?[\s-]?\d{4}[\s-]?\d{4}))(?:[\s-]?(?:x|ext\.?|\#)\d{3,4})?$/,
      'Please enter a valid UK phone number'
    ),
  lives_in_england_wales: yup.boolean().nullable().notRequired(),
  assets_in_uk: yup.boolean().nullable().notRequired(),
  owns_business: yup.boolean().nullable().notRequired(),
  source: yup.string().required('Source is required'),
  notes: yup.string().notRequired(),
});

export type WillData = yup.InferType<typeof willSchema>;
export type CallbackRequestData = yup.InferType<typeof callbackRequestSchema>;
export type WillCallbackRequestData = yup.InferType<typeof willCallbackRequestSchema>;
